import {
  VORHABEN_ZUSTAND_ERKLAERUNG,
  vorhabenZustand,
  zustandsZaehlung,
  type VorhabenZustand,
} from '@netcon-work/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { Topbar } from '../components/Topbar'
import { VorhabenDetail } from '../components/VorhabenDetail'
import { VorhabenZeile, Zustandsmarke, type VorhabenEintrag } from '../components/VorhabenZeile'
import { Button, Card, CardPad } from '../components/ui/primitives'
import { nachflussVorschauQuery, vorhabenQuery } from '../lib/queries'
import { trpc } from '../lib/trpc'

/**
 * **Vorhaben** (Phase 8, Mockup `v-vorhaben`, Konzept Kap. 2).
 *
 * Die inhaltliche Klammer – vom Mini-Thema bis zum Projekt. Ein Vorhaben trägt Typ,
 * Entwicklungsart, Mit-Owner, Wunsch-Entwickler, Zieltermin **und die Pool-Anbindung, über die
 * seine bereiten Aufgaben nachfließen**.
 *
 * ## Warum der Nachfluss hier steht und nicht in den Pools
 *
 * Nils, 03.08.2026: *„wie kann ich das optimieren, dass in Zukunft immer automatischer
 * zugeordnet wird – und wenn nicht, muss es eben von den Entwicklern manuell geholt oder
 * zugeordnet werden."*
 *
 * Beides steckt in dieser einen Einstellung. Ein Vorhaben schiebt seine bereiten Aufgaben
 * selbständig in seinen Pool, **aber nur bis zu einer Grenze**. Was nicht nachfließt, holt sich
 * jemand von Hand – dieselbe Bewegung wie bisher, nur seltener nötig.
 *
 * Die Grenze gehört ans Vorhaben und nicht an den Pool: der Pool hat sein WIP-Limit, aber das
 * begrenzt die **Arbeit**, nicht den **Vorrat**. Ohne Grenze je Vorhaben schüttet eines mit 40
 * bereiten Aufgaben den Pool zu, und die anderen kommen dort nicht mehr vor.
 *
 * ## Seit dem 26.08.2026 eine Liste statt eines Kartenstapels
 *
 * Nils, mit einem Entwurf (`infos/vorhaben-standalone.html`): *„Die Seite ist noch bisschen
 * unübersichtlich."* Der Umbau in drei Teilen:
 *
 * 1. **Zeile statt Karte** ({@link VorhabenZeile}) – 52 px je Vorhaben statt eines halben
 *    Bildschirms.
 * 2. **Einstellungen ins Panel** (seit 23.10.2026 im einen Vorhaben-Fenster) – die Liste bleibt beim Bearbeiten
 *    stehen, man verliert seine Position nicht.
 * 3. **Zustand statt „laufend"** – ein aus den Daten gerechnetes Wort je Vorhaben, nach dem
 *    gruppiert und gefiltert wird.
 *
 * **Was der Entwurf vorschlug und hier nicht steht:** eine Zeitachse. Sie braucht Startdaten,
 * und die gibt es nicht – `created_at` wäre irreführend (ein im April angelegtes Vorhaben, das
 * im August beginnt, bekäme einen vier Monate langen Balken). Offen als
 * [F-78](../../../../docs/OFFENE-FRAGEN.md).
 */
export function Vorhaben() {
  const queryClient = useQueryClient()
  const { data: vorhaben = [], isLoading } = useQuery(vorhabenQuery)
  const [anlegenOffen, setAnlegenOffen] = useState(false)
  const [detailFuer, setDetailFuer] = useState<string | null>(null)
  const [filter, setFilter] = useState<VorhabenZustand | null>(null)
  const [aufgeklappt, setAufgeklappt] = useState<Record<string, boolean>>({})

  /**
   * **Ein Stichtag für den ganzen Anstrich**, nicht ein `new Date()` je Zeile. Sonst rechnete
   * jede Zeile mit einer minimal anderen Gegenwart – bei „steht seit 21 Tagen" ist das der
   * Unterschied zwischen zwei Zuständen, und welchen man sieht, hinge davon ab, wie lange das
   * Zeichnen gedauert hat.
   *
   * Neu berechnet, sobald sich der Bestand ändert – das ist oft genug: die Schwelle liegt bei
   * drei Wochen, und niemand hat diese Seite drei Wochen offen.
   */
  const jetzt = useMemo(() => new Date(), [vorhaben])

  /**
   * **`?vorhaben=<Kennung>` öffnet das Detail** (21.08.2026) – dieselbe Bauform und derselbe
   * Grund wie `?aufgabe=` am Aufgabendetail: die Benachrichtigungsmail zu einem
   * Vorhaben-Kommentar zeigt hierher, und eine Mail, die nur auf die Übersicht führt,
   * verlangt vom Empfänger, das Vorhaben selbst zu suchen.
   *
   * Gelesen **einmal beim Laden**, nicht bei jeder Adressänderung – sonst ginge das Fenster
   * nach jedem Schließen sofort wieder auf.
   */
  useEffect(() => {
    const gewuenscht = new URLSearchParams(window.location.search).get('vorhaben')
    if (gewuenscht) setDetailFuer(gewuenscht)
  }, [])

  const neuLaden = async () => {
    await queryClient.invalidateQueries({ queryKey: ['vorhaben'] })
    await queryClient.invalidateQueries({ queryKey: ['tasks'] })
    await queryClient.invalidateQueries({ queryKey: ['pools'] })
  }

  const zaehlung = useMemo(() => zustandsZaehlung(vorhaben, jetzt), [vorhaben, jetzt])

  /**
   * **Gruppiert nach Zustand, in der Reihenfolge der Dringlichkeit** – nicht alphabetisch. Was
   * oben steht, will heute etwas von jemandem; „zieht" und „fertig" stehen unten, weil dort
   * nichts zu tun ist.
   */
  const gruppen = useMemo(() => {
    const nach = new Map<VorhabenZustand, VorhabenEintrag[]>()
    for (const v of vorhaben) {
      const z = vorhabenZustand(v, jetzt)
      if (filter && z !== filter) continue
      const liste = nach.get(z)
      if (liste) liste.push(v)
      else nach.set(z, [v])
    }
    return zaehlung
      .filter((e) => nach.has(e.zustand))
      .map((e) => ({ ...e, eintraege: nach.get(e.zustand)! }))
  }, [vorhaben, jetzt, filter, zaehlung])

  const sichtbar = gruppen.reduce((s, g) => s + g.eintraege.length, 0)

  return (
    <>
      <Topbar
        titel="Vorhaben"
        untertitel="Inhaltliche Klammern – vom Mini-Thema bis zum Projekt"
        aktion={
          <Button variant="primary" onClick={() => setAnlegenOffen((v) => !v)}>
            {anlegenOffen ? 'Abbrechen' : '+ Neues Vorhaben'}
          </Button>
        }
      />

      {anlegenOffen && (
        <VorhabenAnlegen
          fertig={async (id) => {
            setAnlegenOffen(false)
            await neuLaden()
            /** Gleich weiter im einen Fenster – dort stehen alle Felder (23.10.2026). */
            setDetailFuer(id)
          }}
        />
      )}

      {vorhaben.length > 0 && (
        <Zustandsleiste
          gesamt={vorhaben.length}
          zaehlung={zaehlung}
          filter={filter}
          setzen={setFilter}
        />
      )}

      <Nachfluss neuLaden={neuLaden} />

      {isLoading && (
        <Card>
          <CardPad className="text-[13px] text-muted">Lädt …</CardPad>
        </Card>
      )}

      {!isLoading && vorhaben.length === 0 && (
        <Card>
          <CardPad className="text-[13px] text-ink2">
            Noch kein Vorhaben. Ein Vorhaben ist die inhaltliche Klammer über Aufgaben – es trägt
            Zieltermin, Mit-Owner und die Pool-Anbindung, über die seine bereiten Aufgaben
            nachfließen.
          </CardPad>
        </Card>
      )}

      {!isLoading && vorhaben.length > 0 && sichtbar === 0 && (
        <Card>
          <CardPad className="text-[13px] text-muted">
            Kein Vorhaben in diesem Zustand.{' '}
            <button
              type="button"
              onClick={() => setFilter(null)}
              className="cursor-pointer text-accent-deep hover:underline"
            >
              Filter zurücksetzen
            </button>
          </CardPad>
        </Card>
      )}

      {gruppen.map((g, i) => (
        <div key={g.zustand} className={i === 0 ? '' : 'mt-5'}>
          {/**
           * **Eine echte Überschrift, kein gestyltes `div`.** Die Liste besteht aus Abschnitten,
           * und ein Screenreader soll sie überspringen können – dieselbe Zusage wie Leitplanke 3
           * für die Tastatur. Nebenbei wird der Abschnitt dadurch prüfbar: „trocken" steht
           * viermal auf der Seite (Filterknopf, Kopf, Zeile am Rechner, Zeile am Telefon), und
           * ein Test über den Text allein misst nicht, was er zu messen behauptet.
           */}
          <h2 className="flex flex-wrap items-center gap-x-2.5 gap-y-1 px-1 pb-2 text-[12px] font-normal">
            <Zustandsmarke zustand={g.zustand} />
            <span className="tabular text-[11px] text-muted">{g.eintraege.length}</span>
            <span className="text-muted">{VORHABEN_ZUSTAND_ERKLAERUNG[g.zustand]}</span>
            <span className="hidden h-px flex-1 bg-line sm:block" />
          </h2>

          <Card>
            {/** Die Kopfzeile nur einmal, über der ersten Gruppe – sie gilt für alle. */}
            {i === 0 && <Spaltenkopf />}
            {g.eintraege.map((v) => (
              <VorhabenZeile
                key={v.id}
                vorhaben={v}
                zustand={g.zustand}
                jetzt={jetzt}
                offen={aufgeklappt[v.id] ?? false}
                umschalten={() => setAufgeklappt((a) => ({ ...a, [v.id]: !a[v.id] }))}
                /** Ein Fenster für alles (23.10.2026) – ⋯ und „öffnen" führen dorthin. */
                bearbeiten={() => setDetailFuer(v.id)}
                oeffnen={() => setDetailFuer(v.id)}
              />
            ))}
          </Card>
        </div>
      ))}

      {detailFuer && (
        <VorhabenDetail vorhabenId={detailFuer} schliessen={() => setDetailFuer(null)} />
      )}

      <p className="mt-4 text-[11.5px] text-muted">
        Ein Vorhaben ist <strong>kein</strong> GitLab-Epic und wird nie eines. Bereiche
        (Repositories) und Vorhaben haben keine Abbildung aufeinander – ein Vorhaben kann mehrere
        Bereiche betreffen, nur einen Teil von einem, oder keinen.
      </p>
    </>
  )
}

/** Die Spaltenbeschriftung – dasselbe Raster wie {@link VorhabenZeile}, nur am Rechner. */
function Spaltenkopf() {
  return (
    <div className="hidden h-[30px] grid-cols-[18px_minmax(150px,1.7fr)_74px_98px_minmax(104px,1fr)_88px_72px_66px_28px] items-center gap-x-3 border-b border-line bg-page px-3.5 text-[9.5px] tracking-[0.1em] text-muted uppercase lg:grid">
      <span />
      <span>Vorhaben</span>
      <span>Typ</span>
      <span>Zustand</span>
      <span>Fortschritt</span>
      <span>Nachfluss</span>
      <span>Ziel</span>
      <span>Wer</span>
      <span />
    </div>
  )
}

/**
 * **Die Zustandsleiste ist gleichzeitig der Filter** (Entwurf).
 *
 * „3 trocken" anzuklicken ist die häufigste Frage des Tages an diese Seite – und sie dauert
 * einen Klick. Zustände ohne Vorhaben kommen nicht vor: ein Knopf „0 steht" lädt zum Klicken
 * auf eine leere Liste ein.
 */
function Zustandsleiste({
  gesamt,
  zaehlung,
  filter,
  setzen,
}: {
  gesamt: number
  zaehlung: { zustand: VorhabenZustand; anzahl: number }[]
  filter: VorhabenZustand | null
  setzen: (z: VorhabenZustand | null) => void
}) {
  return (
    <div className="mb-3 flex flex-wrap items-center gap-1.5">
      <Zaehlknopf an={filter === null} klick={() => setzen(null)}>
        <span className="tabular font-bold">{gesamt}</span> Vorhaben
      </Zaehlknopf>
      <span className="mx-1 hidden h-5 w-px bg-line sm:block" />
      {zaehlung.map((e) => (
        <Zaehlknopf
          key={e.zustand}
          an={filter === e.zustand}
          klick={() => setzen(filter === e.zustand ? null : e.zustand)}
          titel={VORHABEN_ZUSTAND_ERKLAERUNG[e.zustand]}
        >
          <Zustandsmarke zustand={e.zustand} />
          <span className="tabular font-bold">{e.anzahl}</span>
        </Zaehlknopf>
      ))}
    </div>
  )
}

function Zaehlknopf({
  an,
  klick,
  titel,
  children,
}: {
  an: boolean
  klick: () => void
  titel?: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={klick}
      title={titel}
      aria-pressed={an}
      className={`inline-flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1.5 text-[12.5px] transition-colors ${
        an
          ? 'border-ink bg-ink text-page [&_*]:text-page'
          : 'border-line bg-surface text-ink2 hover:border-line2'
      }`}
    >
      {children}
    </button>
  )
}

/**
 * **Der Nachfluss – was er *nicht* tut.**
 *
 * ## Warum diese Karte geschrumpft ist (26.08.2026)
 *
 * Sie zählte bis dahin je Vorhaben auf, was gleich geschoben wird, **und** listete darunter die
 * Hindernisse („3 bereite Aufgaben, aber kein Pool angebunden"). Die Hindernisse stehen jetzt in
 * der Liste selbst – ein Vorhaben ohne Pool oder ohne Grenze ist **trocken** und steht ganz
 * oben, mit Namen, Zahl und einem Weg zur Einstellung. Sie hier ein zweites Mal aufzuzählen
 * wäre dieselbe Auskunft an zwei Orten, und einer davon wird irgendwann nicht mehr gepflegt.
 *
 * Was bleibt, ist das, was die Liste **nicht** sagen kann: wie viele Aufgaben beim nächsten Lauf
 * bewegt werden, und der Handauslöser. Wer gerade ein Limit gesetzt hat, will nicht eine
 * Viertelstunde warten, um zu sehen, ob es wirkt.
 *
 * Seit dem 19.08.2026 schiebt der Nachfluss von selbst – Nils: *automatisch, mit Anzeige*
 * ([E-149](../../../../docs/ENTSCHEIDUNGEN.md)).
 */
function Nachfluss({ neuLaden }: { neuLaden: () => Promise<void> }) {
  const queryClient = useQueryClient()
  const { data: plan } = useQuery(nachflussVorschauQuery)
  const [bericht, setBericht] = useState<string | null>(null)

  const ausfuehren = useMutation({
    mutationFn: () => trpc.vorhaben.nachflussAusfuehren.mutate(),
    onSuccess: async (b) => {
      setBericht(`${b.geschoben} nachgefüllt.`)
      await queryClient.invalidateQueries({ queryKey: ['vorhaben'] })
      await neuLaden()
    },
  })

  if (!plan) return null
  const gesamt = plan.eintraege.reduce((s, e) => s + e.taskIds.length, 0)
  if (gesamt === 0) return null

  return (
    <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-card border border-line bg-surface px-3.5 py-2.5 text-[12.5px]">
      <span className="text-ink2">
        <strong className="tabular">{gesamt}</strong>{' '}
        {gesamt === 1 ? 'bereite Aufgabe wartet' : 'bereite Aufgaben warten'} auf den Nachfluss in{' '}
        {plan.eintraege.length} {plan.eintraege.length === 1 ? 'Vorhaben' : 'Vorhaben'}.
      </span>
      <Button
        variant="ghost"
        size="sm"
        disabled={ausfuehren.isPending}
        onClick={() => {
          setBericht(null)
          ausfuehren.mutate()
        }}
      >
        Jetzt nachfüllen
      </Button>
      {bericht && <span className="text-ink2">{bericht}</span>}
      {/**
       * **Hier stand bis zum 19.08.2026 „Zeigt nur an. Geschoben wird erst auf Knopfdruck."**
       * – und war ab dem Moment falsch, in dem der Nachfluss von selbst läuft
       * ([E-149](../../../../docs/ENTSCHEIDUNGEN.md)). Eine Zusage, die die Oberfläche gibt und
       * die Technik nicht mehr hält, ist schlimmer als keine: sie wird geglaubt.
       */}
      <span className="ml-auto text-[11px] text-muted">
        Läuft alle 15 Minuten von selbst. Der Knopf schiebt sofort.
      </span>
    </div>
  )
}

/**
 * **Neues Vorhaben: Name und Typ, dann weiter im Fenster** (23.10.2026).
 *
 * Nils: *„das Vorhaben anlegen sollte dann aus den Vorhaben-Details die Daten auch haben."* Statt
 * ein zweites Formular mit denselben Feldern zu pflegen, legt dieses nur an und öffnet sofort das
 * eine Vorhaben-Fenster – dort stehen Beschreibung, Termine, Wer, Einladen und Steuerung.
 */
function VorhabenAnlegen({ fertig }: { fertig: (id: string) => Promise<void> }) {
  const [label, setLabel] = useState('')
  const [typ, setTyp] = useState<'projekt' | 'thema'>('thema')
  const [fehler, setFehler] = useState<string | null>(null)

  const anlegen = useMutation({
    mutationFn: () => trpc.vorhaben.anlegen.mutate({ label: label.trim(), typ }),
    onSuccess: (r) => fertig(r.id),
    onError: (e: Error) => setFehler(e.message),
  })

  return (
    <Card className="mb-3">
      <CardPad>
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            setFehler(null)
            anlegen.mutate()
          }}
        >
          <label className="text-[11.5px] font-semibold text-ink2">
            <span className="block text-muted">Name</span>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              required
              autoFocus
              placeholder="Portal 2.0"
              className="mt-0.5 w-[280px] max-w-full rounded-md border border-line px-2 py-1.5 text-[13px] font-normal"
            />
          </label>
          <label className="text-[11.5px] font-semibold text-ink2">
            <span className="block text-muted">Typ</span>
            <select
              value={typ}
              onChange={(e) => setTyp(e.target.value as 'thema')}
              className="mt-0.5 rounded-md border border-line px-2 py-1.5 text-[13px] font-normal"
            >
              <option value="thema">Thema</option>
              <option value="projekt">Projekt</option>
            </select>
          </label>
          <Button type="submit" variant="primary" disabled={anlegen.isPending}>
            Anlegen und ausfüllen
          </Button>
        </form>
        {fehler && <p className="mt-2 text-[12px] text-pool-sofort">{fehler}</p>}
        <p className="mt-2 text-[11px] text-muted">
          Danach öffnet sich das Vorhaben: Beschreibung, Termine, Mit-Owner, Einladen und Pool
          stellst du dort ein.
        </p>
      </CardPad>
    </Card>
  )
}
